import { takeUntil } from 'rxjs/operators';
import { Component, ChangeDetectorRef, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { PasswordModule } from 'primeng/password';
import { TooltipModule } from 'primeng/tooltip';
import { RippleModule } from 'primeng/ripple';
import { DividerModule } from 'primeng/divider';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { AuthService } from '@services/auth.service';
import { SelectLanguageComponent } from '@components/select-language/select-language.component';
import { firstValueFrom, interval, Subscription, Subject } from 'rxjs';
import { environment } from '@root/src/environments/environment';
import {AppLanguageManager, ILanguage, ILanguageCode} from '@components/select-language';
import {LogoComponent} from '@components/logo/logo.component';
import { CustomMessageService } from '@app/interceptors/custom-message';

@Component({
  selector: 'app-login-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    InputTextModule,
    ButtonModule,
    CardModule,
    PasswordModule,
    TooltipModule,
    RippleModule,
    DividerModule,
    SelectLanguageComponent,
    ToastModule,
    LogoComponent
  ],
  providers: [
    {
      provide: MessageService,
      useClass: CustomMessageService,
    },
  ],
  templateUrl: './login-page.html',
  styleUrl: './login-page.scss',
})
export class LoginPageComponent implements OnInit, OnDestroy {
  private static readonly returnUrlStorageKey = 'loginReturnUrl';
  private readonly destroy$ = new Subject<void>();
  private destroyed = false;

  username: string = '';
  password: string = '';
  protected readonly environment = environment;


  twoFactorCode: string = '';
  isTwoFactorStage: boolean = false;
  isLoading: boolean = false;
  resendTimer: number = 0;
  private resendInterval?: Subscription;
  returnUrl: string = '/main-menu';

  get selectedLanguage(): ILanguage {
    return this.languageManager.currentLanguage!;
  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private translate: TranslateService,
    private languageManager: AppLanguageManager,
    private authService: AuthService,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const encodedReturnUrl = params['returnUrl'];
      if (encodedReturnUrl) {
        try {
          const decodedReturnUrl = decodeURIComponent(encodedReturnUrl);
          const urlParams = new URLSearchParams(decodedReturnUrl.split('?')[1] || '');
          const code = urlParams.get('code');
          const callbackReturnUrl = urlParams.get('returnUrl');
          if (code) {
            this.isLoading = true;
            this.cdr.markForCheck();

            this.authService.getTokenByOneId(code).subscribe({
              next: async (response) => {
                if (response?.accessToken) {
                  this.authService.setToken(response.accessToken);
                } else if (response?.access_token) {
                  this.authService.setToken(response.access_token);
                } else if (response?.token) {
                  this.authService.setToken(response.token);
                } else {
                  console.warn('No token found in response, trying to use full response as token');
                  if (typeof response === 'string') {
                    this.authService.setToken(response);
                  }
                }

                const savedToken = this.authService.getToken();
                const decodedToken = this.authService.getDecodedToken();
                if (decodedToken) {
                }

                const allowed = await firstValueFrom(this.authService.hasApplicationAccess().pipe(takeUntil(this.destroy$)), { defaultValue: null });
                if (this.destroyed) return;
                if (allowed === null) { this.onAccessLookupUnavailable(); return; }
                if (!allowed) {
                  const userRoles = this.authService.getUserRoles();
                  console.error('User roles not allowed:', userRoles);
                  this.authService.logout();
                  this.isLoading = false;
                  this.cdr.markForCheck();

                  this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('AccessDenied'),
                    detail:
                      this.translate.instant('InsufficientPermissions') ||
                      'Your role does not have permission to access this system',
                  });
                  return;
                }

                this.messageService.add({
                  severity: 'success',
                  summary: this.translate.instant('Success'),
                  detail: this.translate.instant('LoginSuccessful'),
                });
                this.navigateToReturnUrl(callbackReturnUrl).then(
                  (success) => {
                    this.isLoading = false;
                    this.cdr.markForCheck();
                  },
                  (error) => {
                    console.error('Navigation failed:', error);
                    this.isLoading = false;
                    this.cdr.markForCheck();
                  },
                );
              },
              error: (error) => {
                console.error('### Login failed:', error);
                this.isLoading = false;
                this.cdr.markForCheck();

                const errorMessage = this.getErrorMessage(error);
                const errorSeverity = this.getErrorSeverity(error);
                const errorSummary = this.getErrorSummary(error);

                this.messageService.add({
                  severity: errorSeverity,
                  summary: errorSummary,
                  detail: errorMessage,
                });
              },
            });
          }
        } catch (e) {
          console.error('### Ошибка при декодировании returnUrl:', e);
        }
      }
    });

    this.translate.setDefaultLang(environment.defaultLang);
    this.translate.use(this.selectedLanguage.value);

    this.returnUrl = this.getSafeInternalReturnUrl(this.route.snapshot.queryParams['returnUrl']) || '/main-menu';

    if (this.authService.isAuthenticated()) {
      this.authService.hasApplicationAccess().pipe(takeUntil(this.destroy$)).subscribe(allowed => {
        if (allowed) void this.navigateToReturnUrl();
      });
    }
  }

  private onAccessLookupUnavailable(): void {
    this.isLoading = false;
    this.messageService.add({ severity: 'error', summary: this.translate.instant('AccessDenied'),
      detail: this.translate.instant('ACCESS_LOOKUP_UNAVAILABLE') });
    this.cdr.markForCheck();
  }

  onLanguageChange(language: string): void {
    this.languageManager.changeLanguage(language as ILanguageCode);
    this.translate.use(language);
  }

  onLogin(): void {
    if (!this.username || !this.password) {
      return;
    }

    const trimmedUsername = this.username.trim();

    this.isLoading = true;
    this.cdr.markForCheck();

    this.authService.login(trimmedUsername, this.password).subscribe({
      next: async (response) => {
        localStorage.setItem('username', trimmedUsername);
        if (response?.accessToken) {
          this.authService.setToken(response.accessToken);
        } else if (response?.access_token) {
          this.authService.setToken(response.access_token);
        } else if (response?.token) {
          this.authService.setToken(response.token);
        } else {
          console.warn('No token found in response, trying to use full response as token');
          if (typeof response === 'string') {
            this.authService.setToken(response);
          }
        }

        const savedToken = this.authService.getToken();
        const decodedToken = this.authService.getDecodedToken();
        if (decodedToken) {
        }

        const allowed = await firstValueFrom(this.authService.hasApplicationAccess().pipe(takeUntil(this.destroy$)), { defaultValue: null });
        if (this.destroyed) return;
        if (allowed === null) { this.onAccessLookupUnavailable(); return; }
        if (!allowed) {
          const userRoles = this.authService.getUserRoles();
          console.error('User roles not allowed:', userRoles);
          this.authService.logout();
          this.isLoading = false;
          this.cdr.markForCheck();

          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('AccessDenied'),
            detail:
              this.translate.instant('InsufficientPermissions') ||
              'Your role does not have permission to access this system',
          });
          return;
        }

        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('Success'),
          detail: this.translate.instant('LoginSuccessful'),
        });
        this.navigateToReturnUrl().then(
          (success) => {
            this.isLoading = false;
            this.cdr.markForCheck();
          },
          (error) => {
            console.error('Navigation failed:', error);
            this.isLoading = false;
            this.cdr.markForCheck();
          },
        );
      },
      error: (error) => {
        console.error('Login failed:', error);
        this.isLoading = false;
        this.cdr.markForCheck();

        const errorMessage = this.getErrorMessage(error);
        const errorSeverity = this.getErrorSeverity(error);
        const errorSummary = this.getErrorSummary(error);

        this.messageService.add({
          severity: errorSeverity,
          summary: errorSummary,
          detail: errorMessage,
        });
      },
    });
  }

  onConfirmTwoFactor(): void {
    if (!this.twoFactorCode) {
      return;
    }

    this.isLoading = true;
    this.cdr.markForCheck();

    this.authService.confirmTwoFactor(this.username.trim(), this.twoFactorCode).subscribe({
      next: async (response) => {
        if (response?.accessToken) {
          this.authService.setToken(response.accessToken);
        } else if (response?.access_token) {
          this.authService.setToken(response.access_token);
        } else if (response?.token) {
          this.authService.setToken(response.token);
        } else {
          console.warn('No token found in response, trying to use full response as token');
          if (typeof response === 'string') {
            this.authService.setToken(response);
          }
        }

        const savedToken = this.authService.getToken();
        const decodedToken = this.authService.getDecodedToken();
        if (decodedToken) {
        }

        const allowed = await firstValueFrom(this.authService.hasApplicationAccess().pipe(takeUntil(this.destroy$)), { defaultValue: null });
        if (this.destroyed) return;
        if (allowed === null) { this.onAccessLookupUnavailable(); return; }
        if (!allowed) {
          const userRoles = this.authService.getUserRoles();
          console.error('User roles not allowed:', userRoles);
          this.authService.logout();
          this.isLoading = false;
          this.cdr.markForCheck();

          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('AccessDenied'),
            detail:
              this.translate.instant('InsufficientPermissions') ||
              'Your role does not have permission to access this system',
          });
          return;
        }

        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('Success'),
          detail: this.translate.instant('LoginSuccessful'),
        });
        this.navigateToReturnUrl().then(
          (success) => {
            this.isLoading = false;
            this.cdr.markForCheck();
          },
          (error) => {
            console.error('Navigation failed:', error);
            this.isLoading = false;
            this.cdr.markForCheck();
          },
        );
      },
      error: (error) => {
        console.error('Login failed:', error);
        this.isLoading = false;

        const errorMessage = this.getErrorMessage(error);
        const errorSeverity = this.getErrorSeverity(error);
        const errorSummary = this.getErrorSummary(error);

        this.messageService.add({
          severity: errorSeverity,
          summary: errorSummary,
          detail: errorMessage,
        });
        this.cdr.markForCheck();
      },
    });
  }

  onResendCode(): void {
    if (this.resendTimer > 0) return;
    this.isLoading = true;

    this.authService.login(this.username.trim(), this.password).subscribe({
      next: (response) => {
        this.isLoading = false;
        if (response) {
          this.startResendTimer();
          this.messageService.add({
            severity: 'info',
            summary: this.translate.instant('CodeResent'),
            detail: this.translate.instant('CheckSpam'),
          });
        }
      },
      error: () => {
        this.isLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('Error'),
          detail: this.translate.instant('ResendFailed'),
        });
      },
    });
  }

  onCancelTwoFactor(): void {
    this.isTwoFactorStage = false;
    this.twoFactorCode = '';
    this.resendTimer = 0;
    this.resendInterval?.unsubscribe();
    this.messageService.add({
      severity: 'info',
      summary: this.translate.instant('Cancelled'),
      detail: this.translate.instant('LoginCancelled'),
    });
  }

  private startResendTimer(): void {
    this.resendTimer = 30;
    this.resendInterval?.unsubscribe();
    this.resendInterval = interval(1000).subscribe(() => {
      this.resendTimer--;
      if (this.resendTimer <= 0) {
        this.resendInterval?.unsubscribe();
      }

      this.cdr.markForCheck();
    });
  }

  onOneIdLogin(): void {
    this.isLoading = true;
    sessionStorage.setItem(LoginPageComponent.returnUrlStorageKey, this.returnUrl);

    this.authService.getOneIdRedirectUrl().pipe(takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        let isSafeUrl = this.isSafeExternalUrl(response);

        if (response && this.isSafeExternalUrl(response)) {
          window.location.href = response;
        } else {
          this.isLoading = false;
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('Error'),
            detail: 'Redirect failed',
          });
        }
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('Error'),
          detail: 'Redirect failed',
        });
      },
    });
  }

  private isSafeExternalUrl(url: string): boolean {
    try {
      const parsed = new URL(url);
      return parsed.origin === window.location.origin || environment.trustedHosts.includes(parsed.hostname);
    } catch {
      return false;
    }
  }

  private navigateToReturnUrl(preferredReturnUrl?: string | null): Promise<boolean> {
    const storedReturnUrl = sessionStorage.getItem(LoginPageComponent.returnUrlStorageKey);
    const target =
      this.getSafeInternalReturnUrl(preferredReturnUrl) ||
      this.getSafeInternalReturnUrl(storedReturnUrl) ||
      this.getSafeInternalReturnUrl(this.returnUrl) ||
      '/main-menu';

    sessionStorage.removeItem(LoginPageComponent.returnUrlStorageKey);
    return this.router.navigateByUrl(target);
  }

  private getSafeInternalReturnUrl(value?: string | null): string | null {
    if (!value) {
      return null;
    }

    const candidates = [value];

    try {
      candidates.push(decodeURIComponent(value));
    } catch {
      // The original value is still checked below.
    }

    return candidates.find((candidate) => candidate.startsWith('/') && !candidate.startsWith('//')) ?? null;
  }

  private getErrorMessage(error: any): string {
    if (error?.status) {
      switch (error.status) {
        case 400:
          return this.translate.instant('InvalidCredentials');
        case 401:
          return this.translate.instant('InvalidCredentials');
        case 403:
          if (
            error.error === 'User is locked' ||
            (typeof error.error === 'string' && error.error.includes('User is locked'))
          ) {
            return this.translate.instant('UserLocked');
          }
          return this.translate.instant('AccessForbidden');
        case 404:
          return this.translate.instant('UserNotFound');
        case 500:
          return this.translate.instant('ServerError');
        case 0:
          return this.translate.instant('NetworkError');
        default:
          return this.translate.instant('UnknownError');
      }
    }

    return this.translate.instant('LoginFailed');
  }

  private getErrorSeverity(error: any): 'error' | 'warn' | 'info' {
    if (error?.status) {
      switch (error.status) {
        case 400:
        case 401:
        case 403:
        case 404:
          return 'warn';
        case 500:
        case 0:
          return 'error';
        default:
          return 'error';
      }
    }

    return 'error';
  }

  private getErrorSummary(error: any): string {
    if (error?.status) {
      switch (error.status) {
        case 400:
        case 401:
        case 403:
        case 404:
          return this.translate.instant('AuthenticationError');
        case 500:
          return this.translate.instant('ServerError');
        case 0:
          return this.translate.instant('ConnectionError');
        default:
          return this.translate.instant('Error');
      }
    }

    return this.translate.instant('Error');
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.destroy$.next();
    this.destroy$.complete();
  }

}
